import { Group } from "@mantine/core";
import type { ColumnDef } from "@tanstack/react-table";
import { Mono } from "@/components/Mono";
import { RelatedLink } from "@/components/RelatedLink";
import { AdminActionButton } from "@/components/AdminActionButton";
import { humanize } from "@/lib/format";
import { COLLECTIONS } from "@/firestore/collections";
import type { CardDoc, CardNotificationDoc, WithDocId } from "@/firestore/types";
import type { EntityConfig } from "../types";
import { adminFreezeCard, adminTerminateCard, adminUnfreezeCard } from "@/api/actions";
import { monoCol, relatedCol, stateCol, textCol, timeCol } from "../shared";
import { CardSpendingPolicyBlock } from "./cardSpendingPanel";

type P<T> = WithDocId<T>;

export const cardConfig: EntityConfig<CardDoc> = {
  key: "card",
  collection: COLLECTIONS.cards,
  title: "Card",
  pluralTitle: "Cards",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<CardDoc>>("id", "ID", (r) => r.id),
    stateCol<P<CardDoc>>((r) => r.state),
    textCol<P<CardDoc>>("lastFour", "Last 4", (r) => (r.lastFour ? `•••• ${r.lastFour}` : null)),
    textCol<P<CardDoc>>("cardholder", "Cardholder", (r) => r.cardholderName),
    textCol<P<CardDoc>>("provider", "Provider", (r) => r.provider, { humanize: true }),
    relatedCol<P<CardDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<CardDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Last four", value: d.lastFour ? `•••• ${d.lastFour}` : "—" },
    { label: "Cardholder", value: d.cardholderName },
    { label: "Provider / network", value: `${humanize(d.provider)} · ${humanize(d.network)}` },
    {
      label: "Expiry",
      value: d.expiryMonth && d.expiryYear ? `${String(d.expiryMonth).padStart(2, "0")}/${d.expiryYear}` : "—",
    },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Replaces", value: <RelatedLink entity="card" id={d.replaces} /> },
    { label: "Token wallets", value: (d.tokenWallets ?? []).map(humanize).join(", ") || "—" },
    { label: "Issuance failure", value: d.issuanceFailureReason ?? "—", full: true },
    { label: "Spending limit", value: <CardSpendingPolicyBlock card={d} />, full: true },
  ],
  related: (d) => [
    { title: "Card refunds", entity: "cardRefund", collection: COLLECTIONS.cardRefunds, fkField: "card", value: d.id, orderBy: [["createdAt", "asc"]] },
    {
      title: "Payments",
      entity: "payment",
      collection: COLLECTIONS.payments,
      fkField: "card",
      value: d.id,
      orderBy: [["approvedAt", "desc"]],
    },
    {
      title: "Card notifications",
      entity: "cardNotification",
      collection: COLLECTIONS.cardNotifications,
      fkField: "card",
      value: d.id,
      orderBy: [["createdAt", "desc"]],
    },
  ],
  actions: (d) => (
    <Group gap="xs">
      <AdminActionButton action={adminFreezeCard} params={{ id: d.id, updatedAt: d.updatedAt }} />
      <AdminActionButton action={adminUnfreezeCard} params={{ id: d.id, updatedAt: d.updatedAt }} />
      <AdminActionButton action={adminTerminateCard} params={{ id: d.id, updatedAt: d.updatedAt }} />
    </Group>
  ),
};

export const cardNotificationColumns: ColumnDef<P<CardNotificationDoc>, unknown>[] = [
  textCol<P<CardNotificationDoc>>("type", "Type", (r) => r.providerNotificationType, { humanize: true }),
  textCol<P<CardNotificationDoc>>("title", "Title", (r) => r.title),
  relatedCol<P<CardNotificationDoc>>("card", "Card", "card", (r) => r.card),
  timeCol<P<CardNotificationDoc>>("createdAt", "Created", (r) => r.createdAt),
];
